import { GetSublevelCountsDocument } from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'

import { NodeType, TreeNode, getNodeId, parseTreeNodes } from 'modules/job_catalog/lib/nodesUtils'

interface SublevelCounts {
  families: number
  functions: number
  roles: number
  levels: number
}

interface UseSublevelCountsResult {
  counts: SublevelCounts
  loading: boolean
  error: unknown
}

export const useSublevelCounts = (nodeId: string, nodeType: NodeType): UseSublevelCountsResult => {
  const parsedNodeId = getNodeId(nodeId)

  const { data, loading, error } = useQuery(GetSublevelCountsDocument, {
    variables: {
      nodeId: parsedNodeId,
    },
  })

  const counts: SublevelCounts = {
    families: 0,
    functions: 0,
    roles: 0,
    levels: 0,
  }

  if (data?.jobCatalog?.treesConnection?.nodes) {
    const trees = data.jobCatalog.treesConnection.nodes
    const allNodes: TreeNode[] = []

    trees.forEach((tree: { id: number; serializedTree: string }) => {
      const parsedNodes = parseTreeNodes({ id: tree.id, serializedTree: tree.serializedTree })
      allNodes.push(...parsedNodes)
    })

    const findNodeByIdAndType = (
      nodes: TreeNode[],
      targetId: number,
      targetType: string
    ): TreeNode | null => {
      for (const node of nodes) {
        if (node.id === targetId && node.type.toLowerCase().includes(targetType)) {
          return node
        }
        const found = findNodeByIdAndType(node.children || [], targetId, targetType)
        if (found) return found
      }
      return null
    }

    const targetNode = findNodeByIdAndType(allNodes, parsedNodeId, nodeType)

    if (targetNode) {
      const countDescendants = (node: TreeNode) => {
        const currentNodeType = node.type.toLowerCase()

        if (currentNodeType.includes('family')) {
          counts.families++
        } else if (currentNodeType.includes('function')) {
          counts.functions++
        } else if (currentNodeType.includes('role')) {
          counts.roles++
        } else if (currentNodeType.includes('level')) {
          counts.levels++
        }

        node.children?.forEach(countDescendants)
      }

      targetNode.children?.forEach(countDescendants)
    }
  }

  return {
    counts,
    loading,
    error,
  }
}
